import { CartItem } from '../../models/cart';
import { PaymentMethod, PaymentSettings } from '../../models';
import { getCheckoutTotals } from '../../utils/pricing';
import { generateId, PersistedCheckoutState } from './checkoutStorage';
import { PaymentEntry } from './types';

type PricingSettings = Pick<PaymentSettings, 'pixDiscountPercentage'> | null;

const AMOUNT_TOLERANCE = 0.01;

export function sumEntries(entries: PaymentEntry[]): number {
  return entries.reduce((sum, e) => sum + e.amountPaid, 0);
}

export function createPaymentEntry(method: PaymentMethod, amountPaid: number): PaymentEntry {
  return {
    id: generateId(),
    paymentMethod: method,
    amountPaid,
    installments: method === PaymentMethod.CreditCard ? 1 : undefined,
  };
}

export function createSingleMethodEntry(
  method: PaymentMethod,
  items: CartItem[],
  settings: PricingSettings
): PaymentEntry {
  const { net } = getCheckoutTotals(items, [{ paymentMethod: method }], settings);
  return createPaymentEntry(method, net);
}

export function syncSingleEntry(
  previous: PaymentEntry[],
  next: PaymentEntry[],
  items: CartItem[],
  settings: PricingSettings
): PaymentEntry[] {
  if (next.length !== 1) return next;
  const previousTarget = getCheckoutTotals(items, previous, settings).net;
  if (Math.abs(previousTarget - sumEntries(previous)) >= AMOUNT_TOLERANCE) return next;
  const target = getCheckoutTotals(items, next, settings).net;
  return [{ ...next[0], amountPaid: target }];
}

export function restorePaymentEntries(
  persisted: PersistedCheckoutState,
  items: CartItem[],
  settings: PricingSettings
): PaymentEntry[] | null {
  const entries = persisted.paymentEntries;
  if (!entries || entries.length === 0) return null;

  const totals = getCheckoutTotals(items, entries, settings);
  const restoredTotal = sumEntries(entries);
  const sameCart = persisted.cartTotal != null && Math.abs(persisted.cartTotal - totals.gross) < AMOUNT_TOLERANCE;
  const matchesNet = Math.abs(restoredTotal - totals.net) < AMOUNT_TOLERANCE;

  return sameCart || matchesNet ? entries : null;
}
